import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const API_URLBLAZE = environment.API_URLBLAZE;

export interface ExportTrackingData {
  exportType: string;
  fileName: string;
  recordCount: number;
  appliedFilters?: any;
  module: string;
  description?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExportTrackingService {

  constructor(private http: HttpClient) { }

  /**
   * Track export downloads for audit logging via HTTP interceptor
   * This creates a mock HTTP request that gets caught by the interceptor for audit logging
   * @param trackingData Export tracking information
   * @returns Observable with mock response
   */
  trackExportDownload(trackingData: ExportTrackingData): Observable<any> {
    // Create a mock endpoint URL that matches interceptor's download pattern detection
    const url = `${API_URLBLAZE}/crash-report/download/excel-export`;
    
    // Create payload with export metadata for audit trail
    const exportPayload = {
      exportType: trackingData.exportType,
      fileName: trackingData.fileName,
      recordCount: trackingData.recordCount,
      appliedFilters: trackingData.appliedFilters || {},
      exportTimestamp: new Date().toISOString(),
      actionType: 'DOWNLOAD',
      module: trackingData.module,
      description: trackingData.description || 
        `Exported ${trackingData.recordCount} records as ${trackingData.exportType} file: ${trackingData.fileName}`
    };

    // Make a POST request that will be intercepted for audit logging
    // The actual endpoint doesn't need to exist since we only care about the interceptor logging
    return this.http.post(url, exportPayload).pipe(
      catchError((error) => {
        // Even if the endpoint returns 404, we still want to consider this successful
        // since the main purpose is audit logging via interceptor
        console.log('Export tracking call completed (endpoint may not exist, but audit logged)');
        return of({ success: true, message: 'Export tracked for audit' });
      })
    );
  }

  /**
   * Track CSV export with standardized parameters
   * @param fileName Name of the CSV file
   * @param recordCount Number of records exported
   * @param module Module/component name where export happened
   * @param filters Applied filters during export
   * @returns Observable with tracking response
   */
  trackCSVExport(fileName: string, recordCount: number, module: string, filters?: any): Observable<any> {
    return this.trackExportDownload({
      exportType: 'CSV',
      fileName,
      recordCount,
      module,
      appliedFilters: filters
    });
  }

  /**
   * Track Excel export with standardized parameters
   * @param fileName Name of the Excel file
   * @param recordCount Number of records exported
   * @param module Module/component name where export happened
   * @param filters Applied filters during export
   * @returns Observable with tracking response
   */
  trackExcelExport(fileName: string, recordCount: number, module: string, filters?: any): Observable<any> {
    return this.trackExportDownload({
      exportType: 'Excel',
      fileName,
      recordCount,
      module,
      appliedFilters: filters
    });
  }

  /**
   * Track PDF export with standardized parameters
   * @param fileName Name of the PDF file
   * @param recordCount Number of records exported
   * @param module Module/component name where export happened
   * @param filters Applied filters during export
   * @returns Observable with tracking response
   */
  trackPDFExport(fileName: string, recordCount: number, module: string, filters?: any): Observable<any> {
    return this.trackExportDownload({
      exportType: 'PDF',
      fileName,
      recordCount,
      module,
      appliedFilters: filters
    });
  }
}